import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download, FileIcon, Loader2, UploadCloud, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, BACKEND_URL, catStyle } from "@/lib/api";
import { getToolIcon } from "@/lib/toolIcons";

export default function ToolPage() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [files, setFiles] = useState([]); // {file, file_id, filename, size}
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // {download_url, filename} or {text}
  const [dragOver, setDragOver] = useState(false);
  const [params, setParams] = useState({});
  const [aiSession] = useState(() => Math.random().toString(36).slice(2));
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiHistory, setAiHistory] = useState([]);
  const inputRef = useRef();

  useEffect(() => {
    api.get(`/tools/${toolId}`).then((r) => setTool(r.data)).catch(() => setTool(false));
  }, [toolId]);

  const Icon = tool ? getToolIcon(tool.id, tool.category) : FileIcon;
  const accept = tool?.accepts?.length ? tool.accepts.map((e) => `.${e}`).join(",") : undefined;

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    handleFiles(dropped);
  }, [tool]);

  const handleFiles = async (list) => {
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploaded = r.data.map((d, i) => ({ ...d, orig: list[i] }));
      setFiles((prev) => (tool?.multi ? [...prev, ...uploaded] : uploaded));
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fid) => setFiles((prev) => prev.filter((f) => f.file_id !== fid));

  const runProcess = async () => {
    if (!tool) return;
    if (tool.accepts?.length && files.length === 0 && tool.id !== "password-generator") {
      toast.error("Please upload a file first");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      // AI tools use dedicated endpoints
      if (tool.category === "ai") {
        if (tool.id === "ai-chat") {
          if (!aiQuestion.trim()) { toast.error("Ask a question"); setProcessing(false); return; }
          const r = await api.post("/ai/chat", { file_id: files[0].file_id, question: aiQuestion, session_id: aiSession });
          setAiHistory((h) => [...h, { q: aiQuestion, a: r.data.result }]);
          setAiQuestion("");
        } else if (tool.id === "ai-summarize") {
          const r = await api.post("/ai/summarize", { file_id: files[0].file_id });
          setResult({ text: r.data.result });
        } else if (tool.id === "ai-translate") {
          const r = await api.post("/ai/translate", { file_id: files[0].file_id, target_language: params.target_language || "English" });
          setResult({ text: r.data.result });
        } else if (tool.id === "ai-grammar") {
          const r = await api.post("/ai/grammar", { file_id: files[0].file_id });
          setResult({ text: r.data.result });
        } else if (tool.id === "ai-extract") {
          const r = await api.post("/ai/extract", { file_id: files[0].file_id, instructions: params.instructions || "Extract all key entities and values" });
          setResult({ text: r.data.result });
        }
      } else {
        const fd = new FormData();
        fd.append("file_ids", files.map((f) => f.file_id).join(","));
        fd.append("params", JSON.stringify(params));
        const r = await api.post(`/process/${toolId}`, fd);
        if (r.data.text_result) {
          setResult({ text: JSON.stringify(r.data.result, null, 2) });
        } else {
          setResult({ download_url: r.data.download_url, filename: r.data.filename });
        }
      }
      toast.success("Done!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const startOver = () => { setFiles([]); setResult(null); setAiHistory([]); setParams({}); };

  if (tool === false) return (
    <div className="pt-24 text-center">
      <h1 className="font-cabinet text-3xl font-bold">Tool not found</h1>
      <Button className="mt-4" onClick={() => navigate("/")}>Go home</Button>
    </div>
  );
  if (!tool) return <div className="pt-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-24 pb-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> All tools
        </button>

        {/* Tool header */}
        <div className="mb-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <div className={`inline-flex h-14 w-14 items-center justify-center rounded-xl ${catStyle(tool.category)}`}>
            <Icon className="h-7 w-7" strokeWidth={1.8} />
          </div>
          <div>
            <div className="label-mono mb-1">{tool.category}</div>
            <h1 className="font-cabinet text-3xl font-bold tracking-tight sm:text-4xl">{tool.name}</h1>
            <p className="mt-1 text-muted-foreground">{tool.desc}</p>
          </div>
        </div>

        {!tool.functional && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-brand" />
            <div className="label-mono mt-4">Coming Soon</div>
            <h2 className="mt-2 font-cabinet text-2xl font-semibold">We&apos;re building this tool</h2>
            <p className="mt-2 text-muted-foreground">This tool is on the roadmap. Meanwhile, explore other {tool.category} tools.</p>
            <Button className="mt-6" onClick={() => navigate("/")} data-testid="coming-soon-back">Browse other tools</Button>
          </div>
        )}

        {tool.functional && !result && aiHistory.length === 0 && (
          <>
            {/* Dropzone */}
            {tool.accepts?.length > 0 && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="file-dropzone"
                className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center transition-colors ${dragOver ? "border-brand bg-brand/5" : "border-border hover:border-brand/50 hover:bg-secondary/40"}`}
              >
                <UploadCloud className="h-14 w-14 text-muted-foreground" strokeWidth={1.5} />
                <div className="mt-5 font-cabinet text-xl font-semibold">Drop your files here</div>
                <div className="mt-1 text-sm text-muted-foreground">or click to browse — {tool.multi ? "multiple files allowed" : "single file"}</div>
                <div className="label-mono mt-4">Accepts: {tool.accepts.join(", ")}</div>
                <input ref={inputRef} type="file" accept={accept} multiple={tool.multi} className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} data-testid="file-input" />
              </div>
            )}

            {/* File queue */}
            {files.length > 0 && (
              <div className="mt-6 space-y-2" data-testid="file-queue">
                {files.map((f) => (
                  <div key={f.file_id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary">
                        <FileIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{f.filename}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</div>
                      </div>
                    </div>
                    <button onClick={() => removeFile(f.file_id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-file-${f.file_id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Tool-specific params */}
            <ToolParams tool={tool} params={params} setParams={setParams} />

            {/* Action button */}
            <div className="mt-8 flex items-center justify-end gap-3">
              <Button
                onClick={runProcess}
                disabled={processing || uploading}
                data-testid="run-process-btn"
                className="rounded-full bg-brand px-8 py-6 text-base hover:bg-brand-hover"
              >
                {processing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
                ) : (
                  <>Run {tool.name}</>
                )}
              </Button>
            </div>
          </>
        )}

        {/* AI Chat interface */}
        {tool.id === "ai-chat" && files.length > 0 && (
          <div className="mt-6" data-testid="chat-panel">
            <div className="space-y-3 max-h-96 overflow-y-auto rounded-xl border border-border bg-card p-4">
              {aiHistory.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">Ask your first question about the document.</div>}
              {aiHistory.map((h, i) => (
                <div key={`turn-${i}-${h.q.slice(0, 20)}`} className="space-y-2 animate-fade-up">
                  <div className="rounded-lg bg-secondary/60 p-3 text-sm"><span className="label-mono">You</span><div className="mt-1">{h.q}</div></div>
                  <div className="rounded-lg bg-brand/5 border border-brand/20 p-3 text-sm"><span className="label-mono text-brand">AI</span><div className="mt-1 whitespace-pre-wrap">{h.a}</div></div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input data-testid="chat-input" value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)} placeholder="Ask a question about the document…" onKeyDown={(e) => e.key === "Enter" && runProcess()} />
              <Button data-testid="chat-send" onClick={runProcess} disabled={processing} className="rounded-full bg-brand hover:bg-brand-hover">Ask</Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-6 rounded-xl border border-border bg-card p-8 text-center animate-fade-up" data-testid="result-panel">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" strokeWidth={1.6} />
            <div className="label-mono mt-4">Success</div>
            <h2 className="mt-2 font-cabinet text-2xl font-semibold">Your file is ready</h2>
            {result.download_url && (
              <a
                href={`${BACKEND_URL}${result.download_url}`}
                target="_blank" rel="noreferrer"
                data-testid="download-result-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-medium text-white transition-colors hover:bg-brand-hover"
              >
                <Download className="h-4 w-4" /> Download {result.filename}
              </a>
            )}
            {result.text && (
              <div className="mt-6 max-h-96 overflow-y-auto rounded-lg border border-border bg-secondary/40 p-4 text-left text-sm whitespace-pre-wrap font-plex" data-testid="text-result">
                {result.text}
              </div>
            )}
            <div className="mt-4">
              <Button variant="ghost" size="sm" onClick={startOver} data-testid="start-over-btn">Start over</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolParams({ tool, params, setParams }) {
  const set = (k, v) => setParams({ ...params, [k]: v });
  const cn = "mt-8 space-y-4 rounded-xl border border-border bg-card/50 p-6";

  if (tool.id === "rotate-pdf" || tool.id === "rotate-image") return (
    <div className={cn}>
      <Label>Rotation</Label>
      <Select value={String(params.degrees || 90)} onValueChange={(v) => set("degrees", parseInt(v))}>
        <SelectTrigger data-testid="rotation-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="90">90° right</SelectItem><SelectItem value="180">180°</SelectItem><SelectItem value="270">90° left</SelectItem></SelectContent>
      </Select>
    </div>
  );
  if (tool.id === "compress-pdf") {
    const options = [
      { value: "low", label: "Low compression", hint: "Best quality · larger size" },
      { value: "recommended", label: "Recommended", hint: "Balanced quality and size (default)" },
      { value: "extreme", label: "Extreme compression", hint: "Smallest size · quality may drop" },
    ];
    const current = params.level || "recommended";
    return (
      <div className={cn}>
        <Label>Compression level</Label>
        <RadioGroup value={current} onValueChange={(v) => set("level", v)} className="mt-3 grid gap-2">
          {options.map((o) => {
            const active = current === o.value;
            return (
              <label
                key={o.value}
                htmlFor={`compress-level-${o.value}`}
                data-testid={`compress-level-${o.value}`}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${active ? "border-brand bg-brand/5" : "border-border hover:bg-secondary/50"}`}
              >
                <RadioGroupItem id={`compress-level-${o.value}`} value={o.value} className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium leading-none">{o.label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{o.hint}</div>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>
    );
  }
  if (tool.id === "delete-pages") return (
    <div className={cn}>
      <Label>Pages to delete (comma-separated, e.g. 1,3,5)</Label>
      <Input data-testid="pages-input" value={params.pages || ""} onChange={(e) => set("pages", e.target.value)} placeholder="1,3,5" />
    </div>
  );
  if (tool.id === "add-watermark") return (
    <div className={cn}><Label>Watermark text</Label><Input data-testid="watermark-input" value={params.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="CONFIDENTIAL" /></div>
  );
  if (tool.id === "watermark-image") return (
    <div className={cn}><Label>Watermark text</Label><Input data-testid="image-watermark-input" value={params.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="WATERMARK" /></div>
  );
  if (tool.id === "protect-pdf" || tool.id === "unlock-pdf") return (
    <div className={cn}><Label>Password</Label><Input type="password" data-testid="password-input" value={params.password || ""} onChange={(e) => set("password", e.target.value)} /></div>
  );
  if (tool.id === "resize-image") return (
    <div className={cn + " grid grid-cols-2 gap-3"}>
      <div><Label>Width (px)</Label><Input type="number" data-testid="width-input" value={params.width || 800} onChange={(e) => set("width", parseInt(e.target.value) || 0)} /></div>
      <div><Label>Height (px)</Label><Input type="number" data-testid="height-input" value={params.height || 600} onChange={(e) => set("height", parseInt(e.target.value) || 0)} /></div>
    </div>
  );
  if (tool.id === "compress-image") return (
    <div className={cn}><Label>Quality (10-95)</Label><Input type="number" min="10" max="95" data-testid="quality-input" value={params.quality || 70} onChange={(e) => set("quality", parseInt(e.target.value) || 70)} /></div>
  );
  if (tool.id === "blur-image") return (
    <div className={cn}><Label>Blur radius</Label><Input type="number" data-testid="blur-input" value={params.radius || 5} onChange={(e) => set("radius", parseFloat(e.target.value) || 0)} /></div>
  );
  if (tool.id === "brightness-contrast") return (
    <div className={cn + " grid grid-cols-2 gap-3"}>
      <div><Label>Brightness</Label><Input type="number" step="0.1" data-testid="brightness-input" value={params.brightness || 1.2} onChange={(e) => set("brightness", parseFloat(e.target.value))} /></div>
      <div><Label>Contrast</Label><Input type="number" step="0.1" data-testid="contrast-input" value={params.contrast || 1.2} onChange={(e) => set("contrast", parseFloat(e.target.value))} /></div>
    </div>
  );
  if (tool.id === "ai-translate") return (
    <div className={cn}><Label>Target language</Label><Input data-testid="lang-input" value={params.target_language || ""} onChange={(e) => set("target_language", e.target.value)} placeholder="Spanish, French, Japanese…" /></div>
  );
  if (tool.id === "ai-extract") return (
    <div className={cn}><Label>What to extract</Label><Textarea data-testid="extract-input" value={params.instructions || ""} onChange={(e) => set("instructions", e.target.value)} placeholder="Extract all invoice line items with total…" /></div>
  );
  return null;
}
